import { useCurrentUser } from 'hooks/useCurrentUser';
import { GET_CUSTOM_WORKLISTS } from 'modules/Apollo/queries';
import { useMemo } from 'react';
import {
  CustomWorklist,
  GetCustomWorklistsQuery,
  GetCustomWorklistsQueryVariables,
} from 'generated/graphql';
import { useQuery } from '@apollo/client';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';

export const transformWorklistsToMap = (customWorklists: ReadonlyArray<CustomWorklist>) => {
  const worklists = new Map<string, CustomWorklist>();
  customWorklists.forEach((worklist) => {
    worklists.set(worklist.smid, worklist);
  });

  return worklists;
};

type UseCustomWorklists = {
  privateWorklists: Map<string, CustomWorklist>;
  publicWorklists: Map<string, CustomWorklist>;
  allWorklists: Map<string, CustomWorklist>;
  areCustomWorklistsLoading: boolean;
  refetchWorklists: () => void;
};

export const useCustomWorklists = (options?: { forceFetchQuery?: boolean }): UseCustomWorklists => {
  const { forceFetchQuery = false } = options ?? {};

  const { data: meData, loading: meLoading } = useCurrentUser();
  const userSmid = useMemo(() => meData?.me?.id ?? '', [meData?.me?.id]);
  const [isCustomWorklistsEnabled, isCustomWorklistsEnabledLoading] = useFeatureFlagEnabled(
    FF.CUSTOM_WORKLISTS
  );

  const {
    data: customWorklistsData,
    loading: customWorklistLoading,
    refetch,
  } = useQuery<GetCustomWorklistsQuery, GetCustomWorklistsQueryVariables>(GET_CUSTOM_WORKLISTS, {
    variables: {
      userSmid: userSmid,
    },
    skip:
      !forceFetchQuery &&
      ((!isCustomWorklistsEnabled && !isCustomWorklistsEnabledLoading) ||
        userSmid == null ||
        userSmid === ''),
    fetchPolicy: 'cache-first',
  });

  const privateWorklists = useMemo(() => {
    return transformWorklistsToMap(customWorklistsData?.customWorklists?.privateWorklists ?? []);
  }, [customWorklistsData]);

  const publicWorklists = useMemo(() => {
    return transformWorklistsToMap(customWorklistsData?.customWorklists?.publicWorklists ?? []);
  }, [customWorklistsData]);

  const allWorklists = useMemo(() => {
    return new Map([...privateWorklists, ...publicWorklists]);
  }, [privateWorklists, publicWorklists]);

  return useMemo(
    () => ({
      privateWorklists,
      publicWorklists,
      areCustomWorklistsLoading: meLoading || customWorklistLoading,
      allWorklists,
      refetchWorklists: refetch,
    }),
    [privateWorklists, publicWorklists, meLoading, customWorklistLoading, allWorklists, refetch]
  );
};
